import { useUiLanguage } from "@/i18n/useUiLanguage";
import { tr } from "@/i18n/copy";
import TpsBrand from "@/components/TpsBrand";
import TpsGuideLink from "@/components/TpsGuideLink";
import GoogleSignIn from "@/components/GoogleSignIn";
import {brandAuthPath,brandInvitationDestination} from "@/brandInvitation";
import TpsAccessSplash from "@/components/TpsAccessSplash";
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { errMsg, setAccessToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import LanguageSelector from "@/components/LanguageSelector";
import AuthStudioShell from "@/components/AuthStudioShell";
import { applicationPathForRole } from "@/pwa";

export default function Login() {
  useUiLanguage();
  const { login } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [studioChoices, setStudioChoices] = useState([]);
  const [studioId, setStudioId] = useState(new URLSearchParams(window.location.search).get("studio_id") || "");

  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password, studioId || undefined);
      if (data?.access_token) setAccessToken(data.access_token);
      toast.success(t("auth.welcomeBack"));
      const invitation = brandInvitationDestination(window.location.search);
      if (data?.user?.role === "studio_owner" && new URLSearchParams(window.location.search).get("professional") === "1") nav("/activation?professional=1");
      else if (data?.user?.role === "studio_owner" && invitation) nav(invitation);
      else if (data?.user?.role === "studio_owner" && studioId) nav("/app/owner");
      else nav(applicationPathForRole(data?.user?.role) || "/app");
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (detail?.code === "STUDIO_SELECTION_REQUIRED") {
        setStudioChoices(detail.studios || []);
        setStudioId("");
      } else if (!error?.response && ["ERR_NETWORK", "ECONNABORTED", "ETIMEDOUT"].includes(error?.code)) {
        toast.error(t("auth.connectionError", { defaultValue: "Connessione momentaneamente non disponibile. Controlla la rete e riprova." }));
      } else toast.error(errMsg(error));
    } finally {
      setLoading(false);
    }
  };


  if (loading) return <TpsAccessSplash />;

  return <AuthStudioShell>
    <div className="animate-fade-up">
      <div className="mb-7 flex justify-end"><LanguageSelector authed={false} className="w-36 border-white/15 bg-white/[.04] text-zinc-100" /></div>
      <div className="mb-9 text-center sm:text-left"><TpsBrand /><p className="mt-5 text-xs font-bold uppercase tracking-[.18em] text-[#E10600]">{tr("Tattoo Pro Studio")}</p><h1 className="mt-3 text-3xl font-black tracking-[-.035em] text-white">{tr("Bentornato.")}</h1><p className="mt-2 text-sm text-zinc-400">{t("auth.workspace")}</p></div>
      <form onSubmit={submit} className="space-y-4">
        <div><Label htmlFor="email" className="text-[11px] font-bold uppercase tracking-[.14em] text-zinc-300">{t("fields.email")}</Label><Input id="email" data-testid="login-email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} required className="mt-2 h-11 border-white/15 bg-white/[.045] text-white placeholder:text-zinc-600 focus-visible:ring-[#E10600]" placeholder="you@studio.com" /></div>
        <div><Label htmlFor="password" className="text-[11px] font-bold uppercase tracking-[.14em] text-zinc-300">{tr("Password")}</Label><Input id="password" data-testid="login-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} required className="mt-2 h-11 border-white/15 bg-white/[.045] text-white placeholder:text-zinc-600 focus-visible:ring-[#E10600]" placeholder="••••••••" /></div>
        {studioChoices.length > 0 && <div><Label htmlFor="login-studio">{tr("Scegli lo Studio a cui accedere")}</Label><select id="login-studio" required value={studioId} onChange={event => setStudioId(event.target.value)} className="mt-2 h-11 w-full rounded-md border bg-background px-3"><option value="">{tr("Seleziona Studio")}</option>{studioChoices.map(studio => <option key={studio.studio_id} value={studio.studio_id}>{studio.name}</option>)}</select></div>}
        <Button data-testid="login-submit" type="submit" disabled={loading} className="mt-2 h-12 w-full bg-[#E10600] text-white hover:bg-[#bd0500]">{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{t("auth.signIn")}</Button>
      </form>
      <Link className="mt-4 inline-block text-sm text-zinc-400 underline underline-offset-4" to={`/forgot-password${studioId ? `?studio_id=${encodeURIComponent(studioId)}` : ""}`}>{tr("Password dimenticata?")}</Link>
      <div className="my-6 flex items-center gap-3 text-xs text-zinc-500"><span className="h-px flex-1 bg-white/10" />{t("auth.or")}<span className="h-px flex-1 bg-white/10" /></div>
      <GoogleSignIn data-testid="login-google" />
      <p className="mt-8 text-center text-sm text-zinc-400">{t("auth.newHere")} <Link to={brandAuthPath("register",window.location.search)} className="font-medium text-white underline decoration-[#E10600] underline-offset-4 hover:text-red-200" data-testid="go-register">{t("auth.createStudio")}</Link></p>
      <TpsGuideLink className="mx-auto mt-4 flex w-fit" />
      <p className="mt-9 text-center text-xs tracking-wide text-zinc-500">{tr("Sicuro. Privato. Affidabile.")}</p>
    </div>
  </AuthStudioShell>;
}



